import type { SharedAttributeJSON } from "@/features/shared_attributes/types";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { SurgeryViewT } from "@patient-watch/types/database";
import { buildMissingSideItems } from "./buildMissingSideItems";
import { isFieldCollected } from "./collectionPolicy";
import { isProcedureSideUnset } from "./procedureSide";

/** First step in the patient setup flow when details are missing. */
export type PatientSetupStepPath = "setup/hospital-number" | "setup/reference-date" | "setup/side";

export function patientNeedsHospitalNumberSetup(diary: StatefulDiaryT): boolean {
    if (!isFieldCollected(diary, "hospital_number")) return false;
    const missingDetails = buildMissingDetails(diary);
    return (missingDetails.trackers.missingHospitalNumber?.length ?? 0) > 0;
}

/**
 * Unique shared_attribute_ids on the diary (diary + trackers + events) that still
 * have no procedure date (`reference_date`). The setup flow sets the date on each.
 */
export function buildMissingReferenceDateAttributeIds(diary: StatefulDiaryT): string[] {
    const ids = new Set<string>();
    const add = (sa: SharedAttributeJSON | null | undefined) => {
        if (!sa) return;
        if (sa.reference_date != null) return;
        if (!sa.shared_attribute_id) return;
        ids.add(sa.shared_attribute_id);
    };

    add(diary.shared_attributes as SharedAttributeJSON | undefined);
    for (const t of diary.trackers_view ?? []) {
        add((t as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }
    for (const e of diary.events_view ?? []) {
        add((e as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }

    return [...ids];
}

/**
 * Check if there are ANY missing reference dates in the diary, including
 * those without shared_attribute_ids (which indicates incomplete setup).
 */
export function hasAnyMissingReferenceDate(diary: StatefulDiaryT): boolean {
    const check = (sa: SharedAttributeJSON | null | undefined): boolean => {
        if (!sa) return false;
        return sa.reference_date == null;
    };

    if (check(diary.shared_attributes as SharedAttributeJSON | undefined)) {
        return true;
    }

    for (const t of diary.trackers_view ?? []) {
        if (check((t as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes)) {
            return true;
        }
    }

    for (const e of diary.events_view ?? []) {
        if (check((e as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes)) {
            return true;
        }
    }

    return false;
}

/** Patient must complete `/setup/reference-date` while a procedure date is missing (unless skipped for now). */
export function patientNeedsReferenceDateSetup(diary: StatefulDiaryT): boolean {
    if (!hasAnyMissingReferenceDate(diary)) return false;
    if (diary.reference_date_setup_skipped) return false;
    return true;
}

/** A skipped date does not block forms, but Setup stays incomplete until the date is supplied. */
export function patientHasSkippedReferenceDateSetup(diary: StatefulDiaryT): boolean {
    return hasAnyMissingReferenceDate(diary) && Boolean(diary.reference_date_setup_skipped);
}

/**
 * Next set-up step, or nowhere if set-up can be left alone.
 * "I don't know yet" on procedure date counts as done so the patient moves on.
 * Order: hospital number → procedure date → side.
 */
export function getPatientFirstSetupStepPath(diary: StatefulDiaryT): PatientSetupStepPath | null {
    if (patientNeedsHospitalNumberSetup(diary)) return "setup/hospital-number";
    if (patientNeedsReferenceDateSetup(diary)) return "setup/reference-date";
    if (buildMissingSideItems(diary).length > 0) return "setup/side";
    return null;
}

export function isInjectionOrthopaedicUnset(area: string | null | undefined): boolean {
    if (area == null) return true;
    return String(area).trim() === "";
}

/** Placeholder stored when the patient skips entering a hospital number. */
export const SKIPPED_HOSPITAL_NUMBER = "XXXX";

export function isHospitalNumberMissing(value: string | null | undefined): boolean {
    if (value == null) return true;
    return String(value).trim() === "";
}

export function isSkippedHospitalNumber(value: string | null | undefined): boolean {
    if (value == null) return false;
    return String(value).trim().toUpperCase() === SKIPPED_HOSPITAL_NUMBER;
}

/** True when a hospital number should be shown in the UI (not empty and not the skip placeholder). */
export function isDisplayableHospitalNumber(value: string | null | undefined): boolean {
    if (isHospitalNumberMissing(value)) return false;
    return !isSkippedHospitalNumber(value);
}

export interface MissingDetailsSnapshot {
    trackers: {
        missingDate: unknown[];
        missingHospitalNumber: unknown[];
    };
    injections: {
        missingSide: unknown[];
        missingOrthopaedicArea: unknown[];
    };
    surgeries: {
        missingSide: SurgeryViewT[];
    };
    shockwaves: {
        missingSide: unknown[];
        missingOrthopaedicArea: unknown[];
    };
}

export function buildMissingDetails(diary: StatefulDiaryT): MissingDetailsSnapshot {
    const collectHospitalNumber = isFieldCollected(diary, "hospital_number");
    return {
        trackers: {
            missingDate: [
                ...(diary.shared_attributes?.reference_date === null ? [diary.shared_attributes] : []),
                ...(diary.trackers_view?.filter((tracker) => tracker?.shared_attributes?.reference_date === null) ||
                    []),
                ...(diary.events_view?.filter((event) => event?.shared_attributes?.reference_date === null) || []),
            ],
            missingHospitalNumber: collectHospitalNumber
                ? [
                      ...(diary.shared_attributes && isHospitalNumberMissing(diary.shared_attributes.hospital_number)
                          ? [diary.shared_attributes]
                          : []),
                      ...(diary.trackers_view?.filter((tracker) =>
                          tracker?.shared_attributes
                              ? isHospitalNumberMissing(tracker.shared_attributes.hospital_number)
                              : false
                      ) ?? []),
                      ...(diary.events_view?.filter((event) =>
                          event?.shared_attributes
                              ? isHospitalNumberMissing(event.shared_attributes.hospital_number)
                              : false
                      ) ?? []),
                  ]
                : [],
        },
        injections: {
            missingSide:
                diary.events_view
                    ?.flatMap((event) =>
                        event?.injections_view?.filter((injection) =>
                            injection?.injection_details?.some((detail) => isProcedureSideUnset(detail?.side))
                        )
                    )
                    .filter(Boolean) ?? [],
            missingOrthopaedicArea:
                diary.events_view
                    ?.flatMap((event) =>
                        event?.injections_view?.filter((injection) =>
                            injection?.injection_details?.some((detail) =>
                                isInjectionOrthopaedicUnset(detail?.orthopaedic_area)
                            )
                        )
                    )
                    .filter(Boolean) ?? [],
        },
        surgeries: {
            missingSide:
                diary.events_view
                    ?.flatMap((event) => event?.surgeries_view ?? [])
                    .filter((s): s is SurgeryViewT => s != null && isProcedureSideUnset(s.side)) ?? [],
        },
        shockwaves: {
            missingSide:
                diary.events_view
                    ?.flatMap((event) =>
                        event?.shockwaves_view?.filter((sw) =>
                            sw?.shockwave_details?.some((d) => isProcedureSideUnset(d?.side))
                        )
                    )
                    .filter(Boolean) ?? [],
            missingOrthopaedicArea:
                diary.events_view
                    ?.flatMap((event) =>
                        event?.shockwaves_view?.filter((sw) =>
                            sw?.shockwave_details?.some((d) => isInjectionOrthopaedicUnset(d?.orthopaedic_area))
                        )
                    )
                    .filter(Boolean) ?? [],
        },
    };
}

/** Same row resolution as MissingDiaryDetailsCall for hospital number editing. */
export function sharedAttributesFromMissingRow(
    diary: StatefulDiaryT,
    missingList: unknown[]
): SharedAttributeJSON | undefined {
    const first = missingList[0];
    if (!first || typeof first !== "object") {
        return diary.shared_attributes as SharedAttributeJSON | undefined;
    }
    const o = first as Record<string, unknown>;
    if ("shared_attributes" in o && o.shared_attributes && typeof o.shared_attributes === "object") {
        return o.shared_attributes as SharedAttributeJSON;
    }
    if ("shared_attribute_id" in o) {
        return o as unknown as SharedAttributeJSON;
    }
    return diary.shared_attributes as SharedAttributeJSON | undefined;
}

export function getSharedAttributesForHospitalPrompt(diary: StatefulDiaryT): SharedAttributeJSON | undefined {
    const missingDetails = buildMissingDetails(diary);
    return sharedAttributesFromMissingRow(diary, missingDetails.trackers.missingHospitalNumber ?? []);
}

/** Patient must complete `/setup/side` while side details are missing. */
export function patientNeedsSideSetupRoute(diary: StatefulDiaryT): boolean {
    return buildMissingSideItems(diary).length > 0;
}
